import type { ComponentType } from "react";
import type { AppView, Role } from "@/contracts";
import type { IconProps } from "@tabler/icons-react";
import {
  IconAddressBook,
  IconAlertTriangle,
  IconAntenna,
  IconArrowsExchange,
  IconBell,
  IconBuilding,
  IconClipboardList,
  IconCpu,
  IconFileText,
  IconLayoutDashboard,
  IconMapPin,
  IconPackage,
  IconRouter,
  IconAntennaOff,
  IconUser,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";

export type NavIcon = ComponentType<IconProps>;

export interface NavItem {
  to: string;
  /** i18n key under the `nav` namespace. */
  labelKey: string;
  /** Roles allowed to see the entry. The API enforces the same rule again. */
  allowed: readonly Role[];
  /**
   * Membership view required for this entry (ADR-088). Absent for profile
   * and for SUPER_ADMIN-only platform screens that are not grantable.
   * Ignored when `anyOfViews` is set.
   */
  view?: AppView;
  /**
   * Show the entry when the membership holds any of these views (OR).
   * Used by hubs that span more than one grantable module.
   */
  anyOfViews?: readonly AppView[];
  /**
   * Extra path prefixes that keep this entry highlighted (e.g. detail routes
   * that still live outside the hub path).
   */
  alsoActiveFor?: readonly string[];
  /** Decorative icon; the link text remains the accessible name. */
  icon: NavIcon;
}

const EVERYONE: readonly Role[] = ["USER", "ADMIN", "SUPER_ADMIN"];
const ADMINS: readonly Role[] = ["ADMIN", "SUPER_ADMIN"];

/**
 * Only routes that exist today.
 *
 * Incidents sit second, directly under the dashboard, because they are the
 * one entry somebody opens this application in a hurry to reach. Everything
 * after that is ordered the way the domain nests — devices sit on assets,
 * assets sit on sites, and gateways sit alongside sites because that is where
 * they are physically mounted — with the reference page and the
 * administrative entries last.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: "/dashboard",
    labelKey: "dashboard",
    allowed: EVERYONE,
    view: "DASHBOARD",
    icon: IconLayoutDashboard,
  },
  {
    to: "/incidents",
    labelKey: "incidents",
    allowed: EVERYONE,
    view: "INCIDENTS",
    icon: IconAlertTriangle,
  },
  { to: "/devices", labelKey: "devices", allowed: EVERYONE, view: "DEVICES", icon: IconCpu },
  { to: "/assets", labelKey: "assets", allowed: EVERYONE, view: "ASSETS", icon: IconPackage },
  { to: "/sites", labelKey: "sites", allowed: EVERYONE, view: "SITES", icon: IconMapPin },
  {
    to: "/gateways",
    labelKey: "gateways",
    allowed: EVERYONE,
    view: "GATEWAYS",
    icon: IconAntenna,
  },
  {
    to: "/contracts",
    labelKey: "contracts",
    allowed: EVERYONE,
    anyOfViews: ["LOAN_AGREEMENTS", "SERVICE_AGREEMENTS"],
    alsoActiveFor: ["/loan-agreements", "/service-agreements"],
    icon: IconFileText,
  },
  {
    to: "/onboarding",
    labelKey: "onboarding",
    allowed: ADMINS,
    anyOfViews: ["DEVICES", "LOAN_AGREEMENTS", "SERVICE_AGREEMENTS"],
    icon: IconUserPlus,
  },
  {
    to: "/supported-devices",
    labelKey: "supportedDevices",
    allowed: EVERYONE,
    view: "SUPPORTED_DEVICES",
    icon: IconRouter,
  },
  { to: "/users", labelKey: "users", allowed: ADMINS, view: "USERS", icon: IconUsers },
  {
    to: "/alert-rules",
    labelKey: "alertRules",
    allowed: ADMINS,
    view: "ALERT_RULES",
    icon: IconBell,
  },
  {
    to: "/contacts",
    labelKey: "contacts",
    allowed: ADMINS,
    view: "CONTACTS",
    icon: IconAddressBook,
  },
  {
    to: "/webhook-events",
    labelKey: "webhookEvents",
    allowed: ADMINS,
    view: "WEBHOOK_EVENTS",
    icon: IconAntennaOff,
  },
  { to: "/audit", labelKey: "audit", allowed: ADMINS, view: "AUDIT", icon: IconClipboardList },
  { to: "/organizations", labelKey: "organizations", allowed: ["SUPER_ADMIN"], icon: IconBuilding },
  {
    to: "/network-sync",
    labelKey: "networkSync",
    allowed: ["SUPER_ADMIN"],
    icon: IconArrowsExchange,
  },
  { to: "/profile", labelKey: "profile", allowed: EVERYONE, icon: IconUser },
];
